import {
  ApolloError,
  ApolloQueryResult,
  DocumentNode,
  gql,
  OperationVariables,
  QueryHookOptions,
} from '@apollo/client';
import { MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { Locale } from 'modules/domains/template';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';

export const queryTemplateLocales = gql`
  query getTemplateLocales($id: Long!) {
    template(id: $id) {
      ... on Template {
        locales {
          tag
          languageName
        }
        messageMedium
      }
    }
  }
`;

type QueryTemplateLocalesResponse = {
  template: {
    locales: Locale[];
    messageMedium: MessageMedium | WhatsAppMedium;
  };
};

export type UseQueryTemplateLocalesState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloQueryResult<QueryTemplateLocalesResponse>>;
  templateLocales: Locale[];
  messageMedium?: MessageMedium | WhatsAppMedium;
  /**
   * @deprecated use `templateLocales` instead
   */
  results: Locale[];
};

type QueryTemplateLocalesVariables = OperationVariables;
export type QueryTemplateLocalesOptions = QueryHookOptions<
  QueryTemplateLocalesResponse,
  QueryTemplateLocalesVariables
>;

export const useQueryTemplateLocales = (
  options: QueryTemplateLocalesOptions,
  query: DocumentNode = queryTemplateLocales,
): UseQueryTemplateLocalesState => {
  const queryOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      ...options,
    }),
    [options],
  );

  const {
    data,
    errorMessage,
    isLoading,
    error: hasError = false,
    refetch,
  } = useGraphql<QueryTemplateLocalesResponse, QueryTemplateLocalesVariables>(query, queryOptions);

  const templateLocales = data?.template?.locales ?? [];
  const messageMedium = data?.template?.messageMedium;

  return {
    errorMessage,
    hasError,
    isLoading,
    refetch,
    templateLocales,
    results: templateLocales,
    messageMedium,
  };
};
